import * as React from "react";
import { Slider } from "../ui/Slider";
import { NumberInput } from "../ui/Input";
import { Icon } from "../ui/Icon";
import { cn } from "../../lib/utils";
import { FixedField } from "../../utils/needMath";
import Chain from "../../icons/chain.svg?react";

type Tone = "fat" | "carbs" | "protein";
type IconTone = "gold" | "teal" | "protein";

const toneClass: Record<
  Tone,
  { text: string; chip: string; autoButton: string; iconTone: IconTone }
> = {
  fat: {
    text: "text-gold",
    chip: "text-gold",
    autoButton: "border-gold",
    iconTone: "gold",
  },
  carbs: {
    text: "text-teal",
    chip: "text-teal",
    autoButton: "border-teal",
    iconTone: "teal",
  },
  protein: {
    text: "text-protein",
    chip: "text-protein",
    autoButton: "border-protein",
    iconTone: "protein",
  },
};

const macroLabel: Record<Tone, string> = {
  fat: "Fett",
  carbs: "KH",
  protein: "Protein",
};

const percentField: Record<Tone, FixedField> = {
  fat: "fatPercent",
  carbs: "carbsPercent",
  protein: "proteinPercent",
};

export interface MacroRowProps {
  tone: Tone;
  grams: number;
  percent: number;
  isAuto: boolean;
  onPercentChange: (percent: number) => void;
  onGramsChange: (grams: number) => void;
  onToggleAuto: () => void;
}

export const MacroRow = ({
  tone,
  grams,
  percent,
  isAuto,
  onPercentChange,
  onGramsChange,
  onToggleAuto,
}: MacroRowProps) => {
  const classes = toneClass[tone];
  const fieldId = `macro-${tone}`;
  const clampedPercent = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className={cn("font-bold", classes.text)}>
          {macroLabel[tone]}
        </span>
        <div className="flex items-center gap-2">
          {isAuto && (
            <span
              className={cn(
                "rounded-pill px-2 py-0.5 text-2xs font-bold uppercase tracking-wider",
                classes.chip,
              )}
            >
              Auto
            </span>
          )}
          <button
            type="button"
            onClick={onToggleAuto}
            aria-pressed={isAuto}
            aria-label={
              isAuto
                ? `${macroLabel[tone]} entkoppeln`
                : `${macroLabel[tone]} als Auto setzen`
            }
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-full border transition",
              isAuto
                ? classes.autoButton
                : "border-line bg-surface-card hover:bg-surface",
            )}
          >
            <Icon size={26} tone={isAuto ? classes.iconTone : "muted"}>
              <Chain />
            </Icon>
          </button>
        </div>
      </div>
      <Slider
        id={fieldId}
        tone={tone}
        min={0}
        max={100}
        step={1}
        value={[clampedPercent]}
        onValueChange={(v) => onPercentChange(v[0])}
        disabled={isAuto}
      />
      <div className="flex items-center gap-2">
        <NumberInput
          value={Math.round(grams * 10) / 10}
          onValueChange={(v) => onGramsChange(v ?? 0)}
          unit="g"
          disabled={isAuto}
          className="text-sm"
        />
        <NumberInput
          value={Math.round(percent * 10) / 10}
          onValueChange={(v) => onPercentChange(v ?? 0)}
          unit="%"
          disabled={isAuto}
          className="text-sm"
        />
      </div>
    </div>
  );
};

export const tonePercentField = percentField;
